import { create } from 'zustand';
import type JawsExport from '@export/three/JawsExport.ts';
import type { JawsCorrectionResponse } from '@/schema/JawsCorrection';
import type { AlignerShellRequest, AlignerShellResult } from '@export/aligner/AlignerShellGenerator';

export type TrimCurvePoint = { x: number; y: number; z: number };
export type TrimCurveJaw = 'upper' | 'lower';
export type TrimCurveState = 'idle' | 'editing' | 'ready';

/** Per-point extra wall thickening (mm), aligned index-for-index to `trimCurves[jaw]`. */
export type TrimThicknessMap = Partial<Record<TrimCurveJaw, number[]>>;

export type AlignerStatus =
  | { state: 'idle' }
  | { state: 'pending' }
  | {
      state: 'success';
      metadata: AlignerShellResult['metadata'];
      jaw: 'upper' | 'lower' | 'both';
      generatedAt: number;
    }
  | { state: 'error'; message: string };

export interface ExportState {
  scanId: string | null;
  setScanId: (scanId: string | null) => void;

  jaws: JawsExport | null;
  setJaws: (jaws: JawsExport | null) => void;

  corrections: JawsCorrectionResponse[];
  setCorrections: (corrections: JawsCorrectionResponse[]) => void;

  activeCorrectionId: string | null;
  setActiveCorrectionId: (id: string | null) => void;

  generateAlignerShell: ((options: AlignerShellRequest) => void) | null;
  setGenerateAlignerShell: (callback: ((options: AlignerShellRequest) => void) | null) => void;

  alignerStatus: AlignerStatus;
  setAlignerStatus: (status: AlignerStatus) => void;

  trimCurves: Partial<Record<TrimCurveJaw, TrimCurvePoint[]>>;
  setTrimCurve: (jaw: TrimCurveJaw, points: TrimCurvePoint[]) => void;
  trimCurveState: TrimCurveState;
  setTrimCurveState: (state: TrimCurveState, jaw?: TrimCurveJaw | null) => void;
  trimEditingJaw: TrimCurveJaw | null;
  generateTrimCurve: ((jaw: TrimCurveJaw) => void) | null;
  setGenerateTrimCurve: ((callback: ((jaw: TrimCurveJaw) => void) | null) => void);
  confirmTrimCurve: (() => void) | null;
  setConfirmTrimCurve: (callback: (() => void) | null) => void;
  cancelTrimCurve: (() => void) | null;
  setCancelTrimCurve: (callback: (() => void) | null) => void;

  /**
   * Trim-curve thickening (post-confirmation phase): the user brushes parts of the confirmed
   * trim curve and assigns extra wall thickness there. `trimThickness` is the persisted
   * per-point value; the rest is interactive editing state.
   */
  trimThickeningActive: boolean;
  setTrimThickeningActive: (active: boolean) => void;
  trimThickeningJaw: TrimCurveJaw | null;
  setTrimThickeningJaw: (jaw: TrimCurveJaw | null) => void;
  trimBrushRadius: number;
  setTrimBrushRadius: (radius: number) => void;
  trimSelectedPoints: number[];
  setTrimSelectedPoints: (indices: number[]) => void;
  trimThickness: TrimThicknessMap;
  trimThicknessValue: number;
  /** Write `value` to every currently-selected point of the active jaw and remember it. */
  applyTrimThickness: (value: number) => void;

  /** Render capped teeth overlay so users can verify the V7 capping seam against the gum. */
  cappingDebugVisible: boolean;
  setCappingDebugVisible: (visible: boolean) => void;

  /** Show V7 shell-band voxel point cloud (generate aligner with this on). */
  bandVoxelDebugVisible: boolean;
  setBandVoxelDebugVisible: (visible: boolean) => void;

  reset: () => void;
}

export const exportStore = create<ExportState>((set) => ({
  scanId: null,
  setScanId: (scanId) => set({ scanId }),

  jaws: null,
  setJaws: (jaws) => set({ jaws }),

  corrections: [],
  setCorrections: (corrections) => set({ corrections }),

  activeCorrectionId: null,
  setActiveCorrectionId: (activeCorrectionId) => set({ activeCorrectionId }),

  generateAlignerShell: null,
  setGenerateAlignerShell: (callback) => set({ generateAlignerShell: callback }),

  alignerStatus: { state: 'idle' },
  setAlignerStatus: (alignerStatus) => set({ alignerStatus }),

  trimCurves: {},
  setTrimCurve: (jaw, points) =>
    set((state) => ({
      trimCurves: { ...state.trimCurves, [jaw]: points },
    })),
  trimCurveState: 'idle',
  trimEditingJaw: null,
  setTrimCurveState: (trimCurveState, jaw) => set({ trimCurveState, trimEditingJaw: jaw ?? null }),
  generateTrimCurve: null,
  setGenerateTrimCurve: (callback) => set({ generateTrimCurve: callback }),
  confirmTrimCurve: null,
  setConfirmTrimCurve: (callback) => set({ confirmTrimCurve: callback }),
  cancelTrimCurve: null,
  setCancelTrimCurve: (callback) => set({ cancelTrimCurve: callback }),

  trimThickeningActive: false,
  setTrimThickeningActive: (trimThickeningActive) => set({ trimThickeningActive }),
  trimThickeningJaw: null,
  setTrimThickeningJaw: (trimThickeningJaw) => set({ trimThickeningJaw }),
  trimBrushRadius: 1.5,
  setTrimBrushRadius: (trimBrushRadius) => set({ trimBrushRadius }),
  trimSelectedPoints: [],
  setTrimSelectedPoints: (indices) =>
    set((state) => {
      const jaw = state.trimThickeningJaw;
      const value =
        jaw && indices.length ? state.trimThickness[jaw]?.[indices[0]] ?? 0 : state.trimThicknessValue;
      return { trimSelectedPoints: indices, trimThicknessValue: value };
    }),
  trimThickness: {},
  trimThicknessValue: 0,
  applyTrimThickness: (value) =>
    set((state) => {
      const jaw = state.trimThickeningJaw;
      if (!jaw) return { trimThicknessValue: value };
      const length = state.trimCurves[jaw]?.length ?? 0;
      const next = (state.trimThickness[jaw] ?? new Array<number>(length).fill(0)).slice();
      for (const i of state.trimSelectedPoints) {
        if (i >= 0 && i < next.length) next[i] = value;
      }
      return { trimThickness: { ...state.trimThickness, [jaw]: next }, trimThicknessValue: value };
    }),

  cappingDebugVisible: false,
  setCappingDebugVisible: (cappingDebugVisible) => set({ cappingDebugVisible }),

  bandVoxelDebugVisible: false,
  setBandVoxelDebugVisible: (bandVoxelDebugVisible) => set({ bandVoxelDebugVisible }),

  reset: () =>
    set({
      scanId: null,
      jaws: null,
      corrections: [],
      activeCorrectionId: null,
      generateAlignerShell: null,
      alignerStatus: { state: 'idle' },
      trimCurves: {},
      trimCurveState: 'idle',
      trimEditingJaw: null,
      generateTrimCurve: null,
      confirmTrimCurve: null,
      cancelTrimCurve: null,
      trimThickeningActive: false,
      trimThickeningJaw: null,
      trimBrushRadius: 1.5,
      trimSelectedPoints: [],
      trimThickness: {},
      trimThicknessValue: 0,
      cappingDebugVisible: false,
      bandVoxelDebugVisible: false,
    }),
}));

